import React, { useState } from 'react';
import { 
  BookOpen, Play, Download, CheckCircle, Clock, 
  ArrowRight, Lightbulb, Target, Users, Star,
  ChevronRight, ChevronDown, Award, Zap
} from 'lucide-react';
import { EnhancedAnalysis, LearningProgress, LearningStep } from '../../types/enhancedAnalysis';

interface LearningPathSectionProps {
  analysis: EnhancedAnalysis;
  userProgress: LearningProgress;
  onStartLearning: () => void;
  onTryTemplate: () => void;
  onViewResources: () => void;
}

const LearningPathSection: React.FC<LearningPathSectionProps> = ({
  analysis,
  userProgress,
  onStartLearning,
  onTryTemplate,
  onViewResources
}) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showAllSteps, setShowAllSteps] = useState(false);

  const learningSteps: LearningStep[] = [
    {
      id: 'understand',
      title: '理解分析框架',
      description: `学习${analysis.methodology.name}的核心概念和应用场景`,
      type: 'understand',
      estimatedTime: 10,
      completed: userProgress.hasViewedMethodology,
      unlocked: true,
      resources: [
        {
          id: '1',
          title: `${analysis.methodology.name}详解`,
          description: '深入了解这个分析框架的理论基础',
          type: 'article',
          url: '#methodology'
        }
      ],
      order: 1
    },
    {
      id: 'practice',
      title: '动手实践分析',
      description: '使用相同的方法论分析类似案例',
      type: 'practice',
      estimatedTime: 30,
      completed: userProgress.hasStartedPractice,
      unlocked: userProgress.hasViewedMethodology,
      resources: [
        {
          id: '2',
          title: '分析模板',
          description: '可下载的分析框架模板',
          type: 'template',
          url: '#template'
        }
      ],
      order: 2
    },
    {
      id: 'apply',
      title: '应用到实际场景',
      description: '将学到的分析方法应用到您的工作或学习中',
      type: 'apply',
      estimatedTime: 60,
      completed: false,
      unlocked: userProgress.hasStartedPractice,
      resources: [
        {
          id: '3',
          title: '实战案例库',
          description: '更多相关案例供您练习',
          type: 'article',
          url: '#cases'
        }
      ],
      order: 3
    }
  ];

  const getStepIcon = (step: LearningStep) => {
    if (step.completed) {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    
    switch (step.type) {
      case 'understand': return <BookOpen className="w-5 h-5 text-blue-600" />;
      case 'practice': return <Play className="w-5 h-5 text-purple-600" />;
      case 'apply': return <Target className="w-5 h-5 text-orange-600" />;
      default: return <Lightbulb className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStepColor = (step: LearningStep) => {
    if (step.completed) {
      return 'bg-green-50 border-green-200';
    }
    if (!step.unlocked) {
      return 'bg-gray-50 border-gray-200';
    }
    
    switch (step.type) {
      case 'understand': return 'bg-blue-50 border-blue-200';
      case 'practice': return 'bg-purple-50 border-purple-200';
      case 'apply': return 'bg-orange-50 border-orange-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const handleStepClick = (step: LearningStep) => {
    if (!step.unlocked) return;
    
    if (step.id === 'understand') {
      onStartLearning();
    } else if (step.id === 'practice') {
      onTryTemplate();
    } else if (step.id === 'apply') {
      onViewResources();
    }
  };

  const nextStep = learningSteps.find(step => step.unlocked && !step.completed);
  const completedSteps = learningSteps.filter(step => step.completed).length;
  const progressPercentage = (completedSteps / learningSteps.length) * 100;

  return (
    <section className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 rounded-xl border border-indigo-200 p-6 mb-8">
      {/* 头部 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center">
            <Lightbulb className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">学习这种分析方法</h3>
            <p className="text-gray-600">掌握{analysis.methodology.name}，提升您的分析能力</p>
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-2xl font-bold text-indigo-600">
            {Math.round(progressPercentage)}%
          </div>
          <div className="text-sm text-gray-500">完成进度</div>
        </div>
      </div>

      {/* 进度条 */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">学习进度</span>
          <span className="text-sm text-gray-500">{completedSteps}/{learningSteps.length} 步骤</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* 下一步推荐 */}
      {nextStep && (
        <div className="bg-white rounded-lg p-4 mb-6 border border-indigo-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Star className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-900">推荐下一步</div>
                <div className="text-sm text-gray-600">{nextStep.title}</div>
              </div>
            </div>
            <button
              onClick={() => handleStepClick(nextStep)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
            >
              开始
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* 学习步骤 */}
      <div className="space-y-4">
        {learningSteps.slice(0, showAllSteps ? undefined : 2).map((step, index) => (
          <div
            key={step.id}
            className={`rounded-lg border-2 transition-all duration-200 ${getStepColor(step)} ${
              step.unlocked ? 'cursor-pointer hover:shadow-md' : 'cursor-not-allowed opacity-60'
            }`}
          >
            <div
              className="p-4"
              onClick={() => step.unlocked && setExpandedStep(expandedStep === step.id ? null : step.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-bold text-gray-500">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    {getStepIcon(step)}
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Clock className="w-4 h-4" />
                        <span>约 {step.estimatedTime} 分钟</span>
                      </div>
                      {step.completed && (
                        <div className="flex items-center space-x-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>已完成</span>
                        </div>
                      )}
                      {!step.unlocked && (
                        <div className="flex items-center space-x-1 text-gray-400">
                          <Award className="w-4 h-4" />
                          <span>需要完成前置步骤</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {step.unlocked && !step.completed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStepClick(step);
                      }}
                      className="px-3 py-1 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      开始
                    </button>
                  )}
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                    expandedStep === step.id ? 'rotate-180' : ''
                  }`} />
                </div>
              </div>
            </div>

            {/* 展开内容 */}
            {expandedStep === step.id && (
              <div className="px-4 pb-4 border-t border-gray-200 bg-white/50">
                <div className="pt-4">
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">学习资源</h5>
                  <div className="space-y-2">
                    {step.resources.map((resource) => (
                      <div key={resource.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                            {resource.type === 'template' ? (
                              <Download className="w-4 h-4 text-gray-600" />
                            ) : (
                              <BookOpen className="w-4 h-4 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{resource.title}</div>
                            <div className="text-xs text-gray-600">{resource.description}</div>
                          </div>
                        </div>
                        <button className="text-indigo-600 hover:text-indigo-700 text-sm font-medium">
                          {resource.type === 'template' ? '下载' : '查看'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* 展开/收起按钮 */}
      {learningSteps.length > 2 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setShowAllSteps(!showAllSteps)}
            className="inline-flex items-center px-4 py-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium transition-colors"
          >
            {showAllSteps ? '收起步骤' : `查看全部 ${learningSteps.length} 个步骤`}
            <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${showAllSteps ? 'rotate-90' : ''}`} />
          </button>
        </div>
      )}

      {/* 底部激励 */}
      <div className="mt-6 pt-6 border-t border-indigo-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900">持续学习，持续成长</div>
              <div className="text-xs text-gray-600">加入 {Math.floor(Math.random() * 1000) + 500}+ 学习者的行列</div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <div className="flex -space-x-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full border-2 border-white flex items-center justify-center">
                  <Users className="w-4 h-4 text-white" />
                </div>
              ))}
            </div>
            <span className="text-xs text-gray-500">活跃学习者</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearningPathSection;